import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { isCannotCreateLeadError } from '@app/routing/RouterToaster'

import { useNavigate } from '@lib/react-router-dom'

import { getRegistrationReferralParams } from '@pages/Referral/helpers'
import { usePersonalDetailsGoBack } from '@pages/Signup/routes/usePersonalDetailsGoBack'

import { SignupFormValues, useSignup } from '@context/SignupContext'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { getTickmillPublicDomain } from '@utils/companyName.utils'
import { getLeadReferrer } from '@utils/cookie.utils'
import { minTwoDigits } from '@utils/validations'

import { ClientAreaLanguageDto } from '@model/ClientAreaLanguageDto'
import { CreateLead } from '@model/CreateLead'

import { validateLanguageId } from '@global/locale/Locale'

import { safeLocalStorage } from '@hooks/useStorage'

import { Loading } from '@shared/components/Loading/Loading'

import { SignupWarningMessages } from '../../../Components/SignupWarningMessages'
import { PersonalDetailsTemplate } from '../PersonalDetailsTemplate'
import {
  PersonalDetailsStep3Form,
  PersonalDetailsStep3FormValues,
} from './PersonalDetailsStep3Form'

enum CreateLeadErrorCode {
  ForbiddenAccess = 'forbidden_access',
  LeadVerificationError = 'lead_verification_error',
}

export const PersonalDetailsStep3Page: React.FC = () => {
  const { signupData, setLeadResponse, resetValues, appendValues } = useSignup()
  const navigate = useNavigate()
  const location = useLocation()
  const goBack = usePersonalDetailsGoBack()
  useEffect(() => {
    const storedCountryId = safeLocalStorage.getItem('countryId')
    if (storedCountryId && !signupData.values?.countryId) {
      appendValues({ countryId: storedCountryId })
    }
  }, [appendValues, signupData])

  useEffect(() => {
    if (signupData.values?.countryId) {
      safeLocalStorage.setItem('countryId', signupData.values.countryId)
    }
  }, [signupData.values?.countryId])

  const apiClient = useApiClient(ClientApiClient)

  const { data = [], isLoading } = useQuery({
    queryKey: queryKeys.signup.countries({ displayAllCountries: true, showHiddenCountries: true }),
    queryFn: () =>
      Promise.all([
        apiClient.getCountries({
          displayAllCountries: true,
          showHiddenCountries: true,
        }),
        apiClient.getClientAreaLanguages({ forSignIn: true, clientAreaVisible: true }),
      ]),
  })
  const [countries = [], languages = []] = data

  const filteredLanguages = languages.filter((language) => language.clientAreaVisible)

  const handleSubmit = async (values: PersonalDetailsStep3FormValues) => {
    try {
      const formData = { ...signupData.values, ...values }
      const resp = await apiClient.createLead(
        dataToSubmit(formData, filteredLanguages, location.search)
      )

      setLeadResponse(resp)
      await appendValues(values)

      navigate(
        `verify-email/confirm?email=${encodeURIComponent(values.email)}&accountTypeId=${
          signupData.values.typeId
        }`
      )
    } catch (err: unknown) {
      const error = err as ResponseError
      const code = error?.response?.response?.data?.code as CreateLeadErrorCode | undefined

      if (isCannotCreateLeadError(code)) {
        resetValues()
      }

      if (code === CreateLeadErrorCode.ForbiddenAccess) {
        const errorSearchParams = new URLSearchParams()
        errorSearchParams.set('error', code)

        navigate(`error?${errorSearchParams.toString()}`, { replace: true })
        return
      }
      await appendValues(values)

      const searchParams = new URLSearchParams()
      searchParams.set('email', values.email)
      searchParams.set('accountTypeId', signupData.values.typeId.toString())
      searchParams.set('error', code || CreateLeadErrorCode.LeadVerificationError)

      navigate(`verify-email/confirm?${searchParams.toString()}`, { replace: true })
    }
  }

  return (
    <PersonalDetailsTemplate reversed step={3} goBack={goBack}>
      <SignupWarningMessages />
      <Loading showLoadingIcon isLoading={isLoading}>
        <PersonalDetailsStep3Form
          countries={countries}
          countryId={signupData.values?.countryId}
          languages={filteredLanguages}
          phoneCodes={countries.map((x) => x.phoneCode)}
          onSubmit={handleSubmit}
        />
      </Loading>
    </PersonalDetailsTemplate>
  )
}

const dataToSubmit = (
  values: SignupFormValues,
  availableLanguages: ClientAreaLanguageDto[] = [],
  search = ''
): CreateLead => {
  const referral = getRegistrationReferralParams(search)

  const languageId = validateLanguageId(values.languageId, availableLanguages)

  return {
    countryId: values.countryId,
    birthDay:
      values.yearOfBirth +
      '-' +
      minTwoDigits(values.monthOfBirth) +
      '-' +
      minTwoDigits(values.dayOfBirth),
    languageId,
    genderId: values.genderId,
    firstName: values.firstName,
    lastName: values.lastName,
    nativeName: values.nativeName,
    middleName: values.middleName,
    email: values.email,
    countryCode: values.countryCode,
    optIn: values.optIn,
    productIds: values.productIds,
    phoneNumber: values.phoneNumber,
    source: 'Live Registration',
    tickmillCompanyId: values.tickmillCompanyId,
    tickmillPublicDomain: getTickmillPublicDomain(),
    typeId: values.typeId.toString(),
    referrer: getLeadReferrer(document.referrer || referral.referrer),
    campaign: referral.utm_campaign,
    campaignSource: referral.utm_source,
    campaignMedium: referral.utm_medium,
    campaignTerm: referral.utm_term,
    campaignContent: referral.utm_content,
    referralCode: referral.referral_code || '',
    affToken: referral.aff_token || '',
    linkClicked: referral.linkClicked || '',
    agreedLegalDocuments: values.agreedLegalDocuments || [],
  }
}
